import { motion } from "framer-motion";
import { Dumbbell, Trophy, Flame, Zap, Coffee } from "lucide-react";
import { Reveal, EASE } from "./Reveal";

const Card = ({ icon: Icon, title, desc, children, className = "", delay = 0, iconClass = "text-acid" }) => (
    <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.15 }}
        transition={{ duration: 0.7, ease: EASE, delay }}
        className={`group h-full rounded-3xl border border-white/[0.07] bg-[#111113] p-6 transition-all duration-300 hover:-translate-y-1 hover:border-acid/35 hover:shadow-[0_20px_60px_rgba(0,0,0,0.5)] md:p-8 ${className}`}
    >
        <div className="mb-5 flex h-11 w-11 items-center justify-center rounded-xl border border-acid/20 bg-acid/10">
            <Icon size={20} className={iconClass} />
        </div>
        <h3 className="font-display text-xl font-extrabold text-white md:text-2xl">
            {title}
        </h3>
        <p className="mt-2.5 text-sm leading-relaxed text-[#8f8f89] md:text-[15px]">
            {desc}
        </p>
        {children}
    </motion.div>
);

export const Features = () => (
    <section
        id="features"
        data-testid="features-section"
        className="relative scroll-mt-20 px-5 py-24 md:py-32"
    >
        <div className="mx-auto max-w-6xl">
            <div className="mb-12 max-w-2xl md:mb-16">
                <Reveal>
                    <p className="mb-4 text-[11px] font-bold tracking-[0.3em] text-acid">
                        WHAT'S INSIDE
                    </p>
                </Reveal>
                <Reveal delay={0.08}>
                    <h2 className="font-display text-4xl font-black leading-[0.98] tracking-[-0.02em] text-white md:text-6xl">
                        Lift. Save.
                        <br />
                        <span className="text-[#C6FF00]">Repeat.</span>
                    </h2>
                </Reveal>
                <Reveal delay={0.16}>
                    <p className="mt-5 text-base text-[#8f8f89] md:text-lg">
                        One app for your workouts, meals and money. Build the
                        body and the bank balance you've always wanted.
                    </p>
                </Reveal>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-12 md:gap-5">
                <Card
                    icon={Dumbbell}
                    title="Macros meet money"
                    desc="What you've eaten and what you've spent, side by side. No switching apps."
                    className="md:col-span-7"
                >
                    <div className="mt-6 space-y-4">
                        <div>
                            <div className="mb-1.5 flex justify-between text-[11px] font-bold tracking-[0.2em] text-white/50">
                                <span>CALORIES</span>
                                <span>1,840 / 2,400 kcal</span>
                            </div>
                            <div className="h-2 overflow-hidden rounded-full bg-white/10">
                                <div className="h-full w-[77%] rounded-full bg-[#C6FF00]" />
                            </div>
                        </div>
                        <div>
                            <div className="mb-1.5 flex justify-between text-[11px] font-bold tracking-[0.2em] text-white/50">
                                <span>BUDGET</span>
                                <span>£62 left this week</span>
                            </div>
                            <div className="h-2 overflow-hidden rounded-full bg-white/10">
                                <div className="h-full w-[38%] rounded-full bg-white/70" />
                            </div>
                        </div>
                    </div>
                </Card>

                <Card
                    icon={Trophy}
                    title="Celebrate every win"
                    desc="Whether it's lifting more or spending less, MoneyMuscle marks the moment."
                    className="md:col-span-5"
                    delay={0.08}
                >
                    <div className="relative mt-6 flex justify-center">
                        <div className="pointer-events-none absolute inset-x-6 top-1/2 h-2/3 -translate-y-1/2 rounded-full bg-[#C6FF00]/15 blur-3xl" />
                        <span className="absolute left-[6%] top-[10%] h-1.5 w-1.5 rotate-12 rounded-[1px] bg-[#C6FF00]/80" />
                        <span className="absolute right-[8%] top-[20%] h-1 w-3 rotate-45 rounded-[1px] bg-[#C6FF00]/70" />
                        <span className="absolute left-[14%] bottom-[16%] h-1 w-2 -rotate-[30deg] rounded-[1px] bg-[#C6FF00]/60" />
                        <span className="absolute right-[14%] bottom-[6%] h-1.5 w-1.5 rounded-full bg-[#C6FF00]/70" />
                        <span className="absolute left-[30%] top-[2%] h-1 w-1 rounded-full bg-[#C6FF00]/50" />
                        <span className="absolute right-[30%] bottom-[0%] h-1 w-2 rotate-12 rounded-[1px] bg-[#C6FF00]/50" />
                        <div className="relative w-full rounded-2xl border border-[#C6FF00] bg-[#15151B] p-5 text-center shadow-[0_0_44px_rgba(198,255,0,0.22)]">
                            <p className="text-[10px] font-bold tracking-[0.28em] text-[#C6FF00]">
                                NEW PERSONAL BEST
                            </p>
                            <p className="font-display mt-2 text-lg font-extrabold text-white md:text-xl">
                                Bench Press
                            </p>
                            <p className="font-display mt-1 text-3xl font-black text-[#C6FF00] md:text-4xl">
                                100 kg
                            </p>
                            <div className="mt-2.5 flex items-center justify-center gap-2">
                                <span className="text-xs text-[#8f8f89]">
                                    Previous best: 95 kg
                                </span>
                                <span className="rounded-full border border-[#C6FF00]/40 bg-[#C6FF00]/15 px-2 py-0.5 text-[10px] font-bold text-[#C6FF00]">
                                    +5 kg
                                </span>
                            </div>
                        </div>
                    </div>
                </Card>

                <Card
                    icon={Zap}
                    title="Log in seconds"
                    desc="Whether it's a set or a coffee — Track it as it happens."
                    className="md:col-span-6"
                >
                    <div className="mt-6 space-y-4">
                        <div className="flex items-center gap-3 rounded-xl border border-white/5 bg-black/40 p-3">
                            <Dumbbell size={16} className="shrink-0 text-[#C6FF00]" />
                            <span className="whitespace-nowrap text-sm text-white/85">
                                Bench Press — 3 sets
                            </span>
                            <span className="ml-auto whitespace-nowrap rounded-full bg-[#C6FF00]/15 px-2.5 py-0.5 text-[11px] font-bold text-[#C6FF00]">
                                LOGGED
                            </span>
                        </div>
                        <div className="flex items-center gap-3 rounded-xl border border-white/5 bg-black/40 p-3">
                            <Coffee size={16} className="shrink-0 text-white/70" />
                            <span className="whitespace-nowrap text-sm text-white/85">
                                Coffee — £3.50
                            </span>
                            <span className="ml-auto whitespace-nowrap rounded-full bg-white/10 px-2.5 py-0.5 text-[11px] font-bold text-white/70">
                                SPENT
                            </span>
                        </div>
                    </div>
                </Card>
                <Card
                    icon={Flame}
                    iconClass="text-[#C6FF00]"
                    title="Streaks that stack"
                    desc="One streak for food, one for training, one for money. Stack them all."
                    className="md:col-span-6"
                    delay={0.08}
                >
                    <div className="relative mt-6">
                        <div className="streak-ring pointer-events-none absolute -inset-[5px] rounded-[19px] border border-[#FFC53D]" />
                        <div className="relative flex items-center gap-3 rounded-[14px] border border-[#FFC53D] bg-[#0A0A0F] px-3.5 py-3">
                            <Flame
                                size={30}
                                className="flame-flicker shrink-0 text-[#FFC53D]"
                            />
                            <div className="min-w-0">
                                <p className="whitespace-nowrap text-[15px] font-bold text-[#FFC53D]">
                                    Blazing gold streak
                                </p>
                                <p className="text-xs text-[#8A8A93]">
                                    3 of 3 streaks active
                                </p>
                            </div>
                            <div className="ml-auto flex shrink-0 items-center gap-1.5">
                                <span className="h-2.5 w-2.5 rounded-full bg-[#FFC53D]" />
                                <span className="h-2.5 w-2.5 rounded-full bg-[#FFC53D]" />
                                <span className="h-2.5 w-2.5 rounded-full bg-[#FFC53D]" />
                            </div>
                        </div>
                    </div>
                </Card>
            </div>
        </div>
    </section>
);
